import { useDataTypesHooks } from "../../../features/data-types"
import { fileHelpers, useFileActions } from "../../../features/files"
import { O } from "../../../features/ui"
import { TableName } from "../../../tables"
import { define, round } from "../../../utils/typeUtils"
import { PaymentStatus, usePriceOutlineHooks, usePricesHooks } from "../../operations"
import { SalePaymentItemData, useCustomerActions, useCustomerBuilders, useCustomerLoaders, useSaleOperationLoaders } from "../../sales"
import { useSaleOperationActions } from "../../sales/actions/saleOperationActions"
import { useSaleOperationBuilders } from "../../sales/builders/saleOperationBuilders"
import { DbWriteOperation, isTransactionCancelledError, useSaveActions } from "../../common"
import { useCreditPaymentActions } from "../actions/creditPaymentActions"
import { useCreditPaymentBuilders } from "../builders/creditPaymentBuilders"
import { useCreditPaymentHooks } from "../hooks/creditPaymentHooks"
import { useCheckLoaders } from "../loaders/checkLoaders"
import { useCreditPaymentLoaders } from "../loaders/creditPaymentLoaders"
import { usePaymentTypeLoaders } from "../loaders/paymentTypeLoaders"
import { useTransactionLoaders } from "../loaders/transactionLoaders"
import { CheckStatus, CreditPayment, CreditPaymentOperationItem, PaymentCategory, PaymentOrderStatus, TransactionDestinationType } from "../state/types"
import { useAdministrationService } from "./administrationService"

export const useCreditPaymentService = () => {
    const { creditPaymentReceiptFilePath } = fileHelpers
    const { getCustomerDebtData, isAccreditationMissing } = useCreditPaymentHooks()
    const { getAdjustedTotal } = usePriceOutlineHooks()
    const pricesHooks = usePricesHooks()
    const { generateCode } = useDataTypesHooks()

    const { buildCreditPayment, buildCreditPaymentOperationItem } = useCreditPaymentBuilders()
    const { buildSaleOperation } = useSaleOperationBuilders()
    const { buildCustomer } = useCustomerBuilders()

    const paymentTypeLoaders = usePaymentTypeLoaders()
    const customerLoaders = useCustomerLoaders()
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()
    const transactionLoaders = useTransactionLoaders()
    const checkLoaders = useCheckLoaders()

    const saveActions = useSaveActions()
    const { saveFile, removeFile } = useFileActions()
    const { saveCreditPayment } = useCreditPaymentActions()
    const { saveSaleOperation } = useSaleOperationActions()
    const { saveCustomer } = useCustomerActions()
    const { accreditItemPayment, confirmItemPayment, createItemCheck } = useAdministrationService()

    const confirmCreditPayment = async (
        customerId: string,
        paymentDate: Date,
        salePaymentItem?: SalePaymentItemData
    ) => {
        const { findPaymentType } = paymentTypeLoaders()
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(customerId, true)
        const { creditInFavor = 0 } = loadedCustomer!
        const debtData = await getCustomerDebtData(customerId, true)
        const operationsData = debtData.operationsData.sort((operationData_A, operationData_B) =>
            operationData_A.operation.creationDate.localeCompare(operationData_B.operation.creationDate)
        )

        let destinationId: O<string>
        let adjustedPaymentAmount = 0
        if (salePaymentItem) {
            const paymentType = findPaymentType(salePaymentItem.paymentTypeId)!
            if (paymentType.category === PaymentCategory.CHECK) {
                const savedCheck = await createItemCheck(salePaymentItem!, paymentDate)
                destinationId = savedCheck.id
            } else {
                destinationId = salePaymentItem.cashFundId
            }
            adjustedPaymentAmount = pricesHooks.adjustment.remove(salePaymentItem.amount, paymentType.yield)
        }

        let availableCredit = creditInFavor
        let availableAdjustedPaymentAmount = adjustedPaymentAmount
        let totalPaidAmount = 0
        const operationItems: CreditPaymentOperationItem[] = []
        let newCreditInFavor = creditInFavor + adjustedPaymentAmount

        for (const operationData of operationsData) {
            const { table, operation, debt } = operationData
            let pendingDebt = debt
            let paidAmount = 0
            
            if (creditInFavor > 0 && availableCredit > 0) {
                const amount = Math.min(availableCredit, pendingDebt)
                pendingDebt = round(pendingDebt - amount)
                availableCredit = round(availableCredit - amount)
                paidAmount = round(paidAmount + amount)
                newCreditInFavor = round(newCreditInFavor - amount)
            }

            if (salePaymentItem && pendingDebt > 0 && availableAdjustedPaymentAmount > 0) {
                const amount = Math.min(availableAdjustedPaymentAmount!, pendingDebt)
                availableAdjustedPaymentAmount = round(availableAdjustedPaymentAmount! - amount)
                paidAmount = round(paidAmount + amount)
                totalPaidAmount = round(totalPaidAmount + amount)
                newCreditInFavor = round(newCreditInFavor - amount)
            }
            
            if (paidAmount > 0) {
                const newPaidAmount = operation.paidAmount + paidAmount
                const isFullyPaid = round(newPaidAmount) >= getAdjustedTotal(table, operation)
                const paidOperation = buildSaleOperation(table, {
                    ...operation,
                    paidAmount: newPaidAmount,
                    paymentStatus: isFullyPaid ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED
                })
    
                await saveSaleOperation(table, paidOperation)
    
                const operationItem = buildCreditPaymentOperationItem({
                    table,
                    operationId: operation.id,
                    amount: paidAmount
                })
                operationItems.push(operationItem)
            }
        }

        const code = await generateCode(TableName.CREDIT_PAYMENTS)
        const creditPayment = buildCreditPayment({
            code,
            paymentTypeId: salePaymentItem?.paymentTypeId,
            destinationId: destinationId,
            amount: salePaymentItem?.amount || 0,
            customerId,
            operationItems,
            status: PaymentOrderStatus.PAYED,
            initialDebt: debtData.debt,
            initialCredit: creditInFavor
        })

        if (salePaymentItem?.receiptFile) {
            const ext = fileHelpers.getFileExtension(salePaymentItem.receiptFile)
            const filePath = creditPaymentReceiptFilePath(creditPayment, ext)
            creditPayment.receiptUrl = await saveFile(filePath, salePaymentItem.receiptFile)
        }

        const savedCreditPayment = await saveCreditPayment(creditPayment)

        // The credit payment is already saved and imputed: if the accreditation fails (server error) the rest still
        // has to be saved, and the caller is told so it can warn that the cash fund movement is missing
        let accreditationFailed = false
        if (salePaymentItem) {
            const paymentType = findPaymentType(salePaymentItem.paymentTypeId)!
            try {
                if ([PaymentCategory.CASH_FUND, PaymentCategory.CARD].includes(paymentType.category)) {
                    await confirmItemPayment(TableName.CREDIT_PAYMENTS, savedCreditPayment.id, salePaymentItem, paymentDate)
                } else if (paymentType.category === PaymentCategory.CHECK) {
                    await accreditItemPayment(TableName.CREDIT_PAYMENTS, savedCreditPayment.id, salePaymentItem.paymentTypeId!, destinationId!, salePaymentItem.amount, paymentDate)
                }
            } catch (error) {
                console.error('Credit payment accreditation failed', savedCreditPayment.id, error)
                accreditationFailed = true
            }
        }

        if (loadedCustomer!.creditInFavor !== newCreditInFavor) {
            const updatedCustomer = buildCustomer({
                ...loadedCustomer,
                creditInFavor: newCreditInFavor
            })
            await saveCustomer(updatedCustomer)
        }

        return {
            creditPayment: savedCreditPayment,
            accreditationFailed
        }
    }

    const uploadPaymentReceipt = async (
        creditPaymentId: string,
        receiptFile: File
    ) => {
        const { loadCreditPayment } = creditPaymentLoaders()
        const loadedCreditPayment = await loadCreditPayment(creditPaymentId, true)

        const ext = fileHelpers.getFileExtension(receiptFile)
        const filePath = creditPaymentReceiptFilePath(loadedCreditPayment!, ext)
        const receiptUrl = await saveFile(filePath, receiptFile)

        const creditPayment = buildCreditPayment({
            ...loadedCreditPayment,
            receiptUrl
        })

        return saveCreditPayment(creditPayment)
    }

    const archiveCreditPayment = async (creditPaymentId: string) => {
        const { loadCreditPayment } = creditPaymentLoaders()
        const creditPayment = await loadCreditPayment(creditPaymentId, true)
        const archivedCreditPayment = buildCreditPayment({
            ...creditPayment,
            archived: true
        })
        return saveCreditPayment(archivedCreditPayment)
    }

    // Returns why the credit payment can't be annulled, or undefined when it can
    const getAnnulBlockReason = async (creditPayment: CreditPayment): Promise<O<string>> => {
        // Later payments were imputed assuming this one was applied, so only the latest one can be annulled
        const { loadCustomersCreditPayments } = creditPaymentLoaders()
        const customerCreditPayments = await loadCustomersCreditPayments([creditPayment.customerId], true)
        const hasLaterPayments = customerCreditPayments.some(customerCreditPayment =>
            customerCreditPayment.id !== creditPayment.id && customerCreditPayment.creationDate > creditPayment.creationDate
        )
        if (hasLaterPayments) {
            return 'El cliente tiene cobros posteriores a este. Para mantener las imputaciones consistentes, primero hay que anular los cobros más recientes.'
        }

        const { findPaymentType } = paymentTypeLoaders()
        const paymentType = findPaymentType(creditPayment.paymentTypeId)
        if (paymentType?.category === PaymentCategory.CHECK) {
            const { loadCheck } = checkLoaders()
            const check = await loadCheck(creditPayment.destinationId!, true)
            if (check && ![CheckStatus.EMITTED, CheckStatus.DEFERRED].includes(check.status)) {
                return 'El cheque recibido en este cobro ya fue usado o cambió de estado, por lo que el cobro no se puede anular.'
            }
        } else if (paymentType && paymentType.accreditationDays > 0) {
            // The scheduled accreditation lives in the backend and can't be cancelled from here
            const { loadSourceTransactions } = transactionLoaders()
            const transactions = await loadSourceTransactions(creditPayment.id, true)
            if (transactions.length === 0) {
                return `El pago con ${paymentType.name} todavía no se acreditó. El cobro se va a poder anular una vez que se acredite.`
            }
        }
    }

    // Reverts everything confirmCreditPayment did, as if the payment had never been made
    const annulCreditPayment = async (creditPaymentId: string) => {
        const { loadCreditPayment } = creditPaymentLoaders()
        const creditPayment = define(await loadCreditPayment(creditPaymentId, true))

        const blockReason = await getAnnulBlockReason(creditPayment)
        if (blockReason) {
            throw new Error(blockReason)
        }

        const { findPaymentType } = paymentTypeLoaders()
        const paymentType = findPaymentType(creditPayment.paymentTypeId)

        // Every write goes in a single transaction, so the annulment is either fully applied or not applied at all
        const operations: DbWriteOperation[] = []

        // The money accredited by the payment is taken back from the cash funds, with atomic decrements
        const { loadSourceTransactions } = transactionLoaders()
        const transactions = await loadSourceTransactions(creditPayment.id, true)
        const cashFundAmounts = new Map<string, number>()
        transactions.forEach(transaction => {
            if (transaction.destinationType === TransactionDestinationType.CASH_FUND && transaction.destinationId) {
                cashFundAmounts.set(transaction.destinationId, (cashFundAmounts.get(transaction.destinationId) || 0) + transaction.amount)
            }
            operations.push({ type: 'delete', table: TableName.TRANSACTIONS, id: transaction.id, mustExist: true })
        })
        cashFundAmounts.forEach((amount, cashFundId) => {
            operations.push({ type: 'increment', table: TableName.CASH_FUNDS, id: cashFundId, increments: { availableAmount: -round(amount) }, mustExist: true })
        })

        if (paymentType?.category === PaymentCategory.CHECK && creditPayment.destinationId) {
            operations.push({ type: 'delete', table: TableName.CHECKS, id: creditPayment.destinationId, mustExist: true })
        }

        // The operations are saved only if their paid amount didn't change since they were loaded
        const { loadSaleOperation } = saleOperationLoaders()
        for (const operationItem of creditPayment.operationItems) {
            const { table, operationId, amount } = operationItem
            const operation = await loadSaleOperation(table, operationId, true)
            if (operation) {
                const newPaidAmount = Math.max(round(operation.paidAmount - amount), 0)
                const isFullyPaid = newPaidAmount >= getAdjustedTotal(table, operation)
                const paymentStatus = newPaidAmount === 0 ? PaymentStatus.PENDING : (isFullyPaid ? PaymentStatus.PAYED : PaymentStatus.PARTIALLY_PAYED)
                const revertedOperation = buildSaleOperation(table, {
                    ...operation,
                    paidAmount: newPaidAmount,
                    paymentStatus
                })
                operations.push({ type: 'put', table, dataType: revertedOperation, expectedValues: { paidAmount: operation.paidAmount } })
            }
        }

        // The payment changed the credit in favor by what was received minus what was imputed to the operations
        const adjustedPaymentAmount = creditPayment.paymentTypeId ? pricesHooks.adjustment.remove(creditPayment.amount, paymentType?.yield || 0) : 0
        const imputedAmount = creditPayment.operationItems.reduce((sum, operationItem) => sum + operationItem.amount, 0)
        const creditInFavorDiff = round(adjustedPaymentAmount - imputedAmount)
        if (creditInFavorDiff !== 0) {
            operations.push({ type: 'increment', table: TableName.CUSTOMERS, id: creditPayment.customerId, increments: { creditInFavor: -creditInFavorDiff }, mustExist: true })
        }

        // If the payment was already annulled (retry or concurrent annulment) the whole transaction is rejected
        operations.push({ type: 'delete', table: TableName.CREDIT_PAYMENTS, id: creditPayment.id, mustExist: true })

        try {
            const { transactWrite } = saveActions()
            await transactWrite(operations)
        } catch (error) {
            if (isTransactionCancelledError(error)) {
                throw new Error('Los datos del cobro cambiaron mientras se anulaba, por lo que no se modificó nada. Recargue la página e intente nuevamente.')
            }
            throw error
        }

        // The annulment is already committed: a receipt that fails to be removed only leaves an orphan file
        if (creditPayment.receiptUrl) {
            try {
                await removeFile(creditPayment.receiptUrl)
            } catch (error) {
                console.error('No se pudo eliminar el comprobante del cobro anulado', error)
            }
        }
    }

    // Accredits again a credit payment whose accreditation failed (the payment and its imputation were saved, but not
    // the cash fund / check movement). It's checked against the db first so the same payment is never accredited twice
    const retryAccreditation = async (creditPayment: CreditPayment) => {
        if (!await isAccreditationMissing(creditPayment, true)) {
            throw new Error('El cobro ya está acreditado')
        }
        const { findPaymentType } = paymentTypeLoaders()
        const paymentType = findPaymentType(creditPayment.paymentTypeId)!
        const paymentDate = new Date(creditPayment.creationDate)
        if (paymentType.category === PaymentCategory.CHECK) {
            await accreditItemPayment(TableName.CREDIT_PAYMENTS, creditPayment.id, paymentType.id, creditPayment.destinationId!, creditPayment.amount, paymentDate)
        } else {
            const paymentItem = { paymentTypeId: paymentType.id, cashFundId: creditPayment.destinationId, amount: creditPayment.amount }
            await confirmItemPayment(TableName.CREDIT_PAYMENTS, creditPayment.id, paymentItem, paymentDate)
        }
    }

    return {
        confirmCreditPayment,
        retryAccreditation,
        uploadPaymentReceipt,
        archiveCreditPayment,
        getAnnulBlockReason,
        annulCreditPayment
    }
}
